/**
 * MarketplacePage — public catalog of installable workflows.
 *
 * Mounted at /projects/:projectId/marketplace (project-scoped so the
 * sidebar context + Deploy target are unambiguous). When the user
 * clicks "Deploy" inside the detail modal we POST to
 * /marketplace/workflows/:slug/deploy with the current projectId →
 * toast on success + close the modal.
 *
 * Layout mirrors the OpenAI GPT marketplace:
 *   - Title + subtitle
 *   - "Trending" section with a two-column numbered list
 *   - Each card = number + icon + name + tagline (2-line truncate) + "By <author>"
 *
 * The detail modal opens on click and shows the full row: icon, title,
 * by-author, description, capabilities, conversation starters,
 * ratings histogram, and a fixed-bottom Deploy button labelled
 * "Deploy to <project name>".
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Marketplace from '../../services/marketplace';
import MarketplaceDetailModal from './MarketplaceDetailModal';
import './MarketplacePage.css';

// Truncate a string to N lines worth of characters as a coarse
// approximation — the CSS line-clamp does the real visual cut. This
// is only here to prevent very-long taglines from breaking layout
// reflow before the clamp kicks in.
const MAX_TAGLINE = 180;

const MarketplacePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { project, setToast } = useOutletContext() || {};

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [activeEntry, setActiveEntry] = useState(null);
  const [deploying, setDeploying] = useState(false);

  // Fetch trending on mount. Pagination is supported by the API but
  // for the initial cut we surface a fixed top-N — adding "load more"
  // is a one-cursor follow-up.
  const fetchTrending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await Marketplace.listTrending(24);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error('Failed to load marketplace:', err);
      setError(err.message || 'Failed to load marketplace');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrending();
  }, [fetchTrending]);

  // ── modal open/close ──────────────────────────────────────────────
  const openDetail = async (entry) => {
    setActiveSlug(entry.slug);
    setActiveEntry(entry); // optimistic — show fields we already have
    // Refresh detail in the background so the modal gets the latest
    // ratings histogram + install count.
    try {
      const fresh = await Marketplace.getWorkflow(entry.slug);
      setActiveEntry(fresh);
    } catch (err) {
      // Non-fatal — modal stays open with the cached row.
      console.warn('Failed to fetch fresh detail for', entry.slug, err);
    }
  };

  const closeDetail = () => {
    setActiveSlug(null);
    setActiveEntry(null);
  };

  // ── deploy ────────────────────────────────────────────────────────
  // opts.warm — integer 1..5 to provision warm Fargate tasks for the
  // deployed workflow (paid feature, ~60s provisioning, eliminates the
  // cold-start delay on trigger). Undefined = cold-start default.
  const handleDeploy = async (entry, opts = {}) => {
    if (!projectId || !entry?.slug) return;
    setDeploying(true);
    try {
      // Thread workflowName when the modal provided one (every modern
      // deploy does). Warm count is forwarded only when explicitly
      // selected via the chevron menu.
      const deployOpts = {};
      if (typeof opts.warm === 'number') deployOpts.warm = opts.warm;
      if (typeof opts.workflowName === 'string' && opts.workflowName.trim()) {
        deployOpts.workflowName = opts.workflowName.trim();
      }
      const res = await Marketplace.deployWorkflow(entry.slug, projectId, deployOpts);
      const projName = project?.name || 'this project';
      const warmSuffix = typeof opts.warm === 'number' && opts.warm > 0
        ? ` with ${opts.warm} warm worker${opts.warm === 1 ? '' : 's'}`
        : '';
      const deployedAs = res?.workflowType || deployOpts.workflowName || entry.slug;
      if (res?.alreadyInstalled) {
        setToast?.({
          isOpen: true,
          message: `${entry.name || entry.slug} is already deployed to ${projName}${warmSuffix}`,
          color: 'warning',
        });
        closeDetail();
        fetchTrending();
      } else {
        setToast?.({
          isOpen: true,
          message: `Deployed ${entry.name || entry.slug} to ${projName} as ${deployedAs}${warmSuffix}`,
          color: 'success',
        });
        closeDetail();
        // Land the user on the new workflow's detail page so they can
        // see provisioning status, connect missing integrations, and
        // trigger the first run. Refresh-on-mount handles the data.
        navigate(`/projects/${projectId}/pipelines/${encodeURIComponent(deployedAs)}`);
      }
    } catch (err) {
      console.error('Deploy failed:', err);
      // 409 from the server (collision with another workflow during the
      // round-trip) — surface the suggested name in the toast so the
      // user knows what to try next.
      const status = err?.statusCode || err?.status;
      const suggestion = err?.data?.suggestion || err?.suggestion;
      const detail = status === 409 && suggestion
        ? `${err.message} — try "${suggestion}"`
        : (err.message || 'unknown error');
      setToast?.({
        isOpen: true,
        message: `Deploy failed: ${detail}`,
        color: 'danger',
      });
    } finally {
      setDeploying(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Marketplace · Zibby</title>
      </Helmet>

      <div className="marketplace-page">
        <header className="marketplace-header">
          <div className="marketplace-title-row">
            <h1 className="marketplace-title">Workflow Marketplace</h1>
          </div>
          <p className="marketplace-subtitle">
            Discover and deploy workflows shared by Zibby and the community.
          </p>
        </header>

        <section className="marketplace-section" aria-label="Marketplace workflows">
          {loading ? (
            <div className="marketplace-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="marketplace-card marketplace-card--skeleton" aria-hidden="true">
                  <span className="marketplace-rank">{i + 1}</span>
                  <div className="marketplace-card-skeleton-icon" />
                  <div className="marketplace-card-skeleton-text" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="marketplace-error" role="alert">
              <p>Couldn't load the marketplace.</p>
              <button type="button" className="marketplace-retry" onClick={fetchTrending}>
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="marketplace-empty">
              <p>No workflows in the marketplace yet — check back soon.</p>
            </div>
          ) : (
            <ol className="marketplace-grid">
              {items.map((entry, idx) => (
                <li key={entry.slug} className="marketplace-card-li">
                  <button
                    type="button"
                    className="marketplace-card"
                    onClick={() => openDetail(entry)}
                    aria-label={`${entry.name} by ${entry.authorName}`}
                  >
                    <span className="marketplace-rank">{idx + 1}</span>
                    <span className="marketplace-card-icon-wrap">
                      {entry.icon ? (
                        // eslint-disable-next-line jsx-a11y/img-redundant-alt
                        <img
                          src={entry.icon}
                          alt=""
                          className="marketplace-card-icon"
                          loading="lazy"
                        />
                      ) : (
                        <span className="marketplace-card-icon marketplace-card-icon--fallback">
                          {(entry.name || entry.slug || '?').slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="marketplace-card-body">
                      <span className="marketplace-card-name">{entry.name || entry.slug}</span>
                      <span className="marketplace-card-tagline">
                        {(entry.tagline || entry.description || '').slice(0, MAX_TAGLINE)}
                      </span>
                      <span className="marketplace-card-author">
                        By {entry.authorName || 'Unknown'}
                        {entry.authorType === 'official' && (
                          <span className="marketplace-card-author-badge">Official</span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {activeSlug && (
        <MarketplaceDetailModal
          entry={activeEntry}
          projectId={projectId}
          projectName={project?.name}
          isOpen={!!activeSlug}
          onClose={closeDetail}
          onDeploy={handleDeploy}
          deploying={deploying}
          onRate={async (entry, n) => {
            try {
              await Marketplace.rateWorkflow(entry.slug, { rating: n });
              // Server already upserts + recomputes histogram. Refetch
              // the detail so myRating + aggregate reflect the new
              // state. Refetch the trending list too so the rank
              // re-orders if the rating bump moved this entry.
              const fresh = await Marketplace.getWorkflow(entry.slug);
              setActiveEntry(fresh);
              fetchTrending();
              setToast?.({
                isOpen: true,
                message: `Thanks — rated ${n} star${n === 1 ? '' : 's'}`,
                color: 'success',
              });
            } catch (err) {
              console.error('Rate failed:', err);
              setToast?.({
                isOpen: true,
                message: `Couldn't save rating: ${err.message || 'unknown error'}`,
                color: 'danger',
              });
            }
          }}
        />
      )}
    </>
  );
};

export default MarketplacePage;
